// Cutlery SVG icons — portrait silhouettes, top-down kitchen-drawer view.
// CutleryDefs must be rendered once at the game root; icons reference its gradient IDs.

export type CutleryType = 'spoon' | 'fork' | 'knife' | 'teaspoon'

/* ── Gradient / shadow definitions ──────────────────────────── */
export function CutleryDefs() {
  return (
    <svg
      width="0"
      height="0"
      aria-hidden
      style={{ position: 'absolute', overflow: 'hidden' }}
    >
      <defs>
        {/* Silver metallic — used by all pieces */}
        <linearGradient id="cutlery-silver" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#888898" />
          <stop offset="30%"  stopColor="#C8C8DC" />
          <stop offset="60%"  stopColor="#E8E8F4" />
          <stop offset="100%" stopColor="#888898" />
        </linearGradient>

        {/* Slightly darker for edges / spine */}
        <linearGradient id="cutlery-silver-dark" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#707080" />
          <stop offset="50%"  stopColor="#B0B0C4" />
          <stop offset="100%" stopColor="#707080" />
        </linearGradient>

        {/* Warm wood for knife handle */}
        <linearGradient id="cutlery-wood" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#5C3A1E" />
          <stop offset="40%"  stopColor="#8B5E3C" />
          <stop offset="70%"  stopColor="#A07040" />
          <stop offset="100%" stopColor="#5C3A1E" />
        </linearGradient>
      </defs>
    </svg>
  )
}

/* ── Spoon ───────────────────────────────────────────────────── */
export function SpoonIcon({ width = 28 }: { width?: number }) {
  const h = Math.round(width * (96 / 28))
  return (
    <svg width={width} height={h} viewBox="0 0 28 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Spoon">
      {/* Oval bowl */}
      <ellipse cx="14" cy="13" rx="11" ry="12" fill="url(#cutlery-silver)" />
      {/* Inner bowl highlight */}
      <ellipse cx="13" cy="12" rx="6" ry="7" fill="url(#cutlery-silver-dark)" opacity="0.25" />
      {/* Neck — narrows from bowl to handle */}
      <path
        d="M9 23 Q9 30 12 31 L12 88 Q12 91 14 91 Q16 91 16 88 L16 31 Q19 30 19 23 Q17 27 14 27 Q11 27 9 23Z"
        fill="url(#cutlery-silver)"
      />
      {/* Handle end cap */}
      <ellipse cx="14" cy="89" rx="3.5" ry="3" fill="url(#cutlery-silver-dark)" opacity="0.6" />
    </svg>
  )
}

/* ── Fork ────────────────────────────────────────────────────── */
export function ForkIcon({ width = 28 }: { width?: number }) {
  const h = Math.round(width * (96 / 28))
  return (
    <svg width={width} height={h} viewBox="0 0 28 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Fork">
      {/* 4 tines */}
      <rect x="4"  y="3" width="4" height="26" rx="2" fill="url(#cutlery-silver)" />
      <rect x="10" y="3" width="4" height="28" rx="2" fill="url(#cutlery-silver)" />
      <rect x="16" y="3" width="4" height="28" rx="2" fill="url(#cutlery-silver)" />
      <rect x="22" y="3" width="4" height="26" rx="2" fill="url(#cutlery-silver)" />
      {/* Shank — tines converge into handle */}
      <path
        d="M4 26 Q4 36 12 37 L12 88 Q12 91 14 91 Q16 91 16 88 L16 37 Q24 36 24 26 L22 30 Q22 34 16 35 L16 37 Q14 37.5 12 37 L12 35 Q6 34 6 30 Z"
        fill="url(#cutlery-silver)"
      />
      {/* Handle end cap */}
      <ellipse cx="14" cy="89" rx="3.5" ry="3" fill="url(#cutlery-silver-dark)" opacity="0.6" />
    </svg>
  )
}

/* ── Knife ───────────────────────────────────────────────────── */
export function KnifeIcon({ width = 28 }: { width?: number }) {
  const h = Math.round(width * (96 / 28))
  return (
    <svg width={width} height={h} viewBox="0 0 28 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Knife">
      {/* Blade: straight left spine, curved right edge tapering to tip */}
      <path
        d="M10 4 L10 40 L18 40 Q22 30 22 18 Q22 8 14 4 Z"
        fill="url(#cutlery-silver)"
      />
      {/* Spine line (left edge of blade) */}
      <rect x="9.5" y="4" width="1.5" height="36" rx="0.5" fill="url(#cutlery-silver-dark)" opacity="0.7" />
      {/* Bolster — metal junction between blade and handle */}
      <rect x="8" y="38" width="14" height="7" rx="2" fill="url(#cutlery-silver-dark)" />
      {/* Handle — warm wood */}
      <rect x="9" y="43" width="12" height="47" rx="5" fill="url(#cutlery-wood)" />
      {/* Handle rivets */}
      <circle cx="15" cy="54" r="1.8" fill="#3A1F0A" opacity="0.55" />
      <circle cx="15" cy="67" r="1.8" fill="#3A1F0A" opacity="0.55" />
      <circle cx="15" cy="80" r="1.8" fill="#3A1F0A" opacity="0.55" />
    </svg>
  )
}

/* ── Teaspoon ────────────────────────────────────────────────── */
// Compact coffee/dessert spoon — rounder bowl, slender handle.
export function TeaspoonIcon({ width = 28 }: { width?: number }) {
  const h = Math.round(width * (96 / 28))
  return (
    <svg width={width} height={h} viewBox="0 0 28 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Teaspoon">
      {/* Rounder, more compact bowl */}
      <ellipse cx="14" cy="12" rx="10" ry="10.5" fill="url(#cutlery-silver)" />
      <ellipse cx="13" cy="11" rx="5.5" ry="5.5" fill="url(#cutlery-silver-dark)" opacity="0.22" />
      {/* Short handle — ends at ~55% of the viewBox height to look clearly smaller than a spoon */}
      <path
        d="M10.5 21 Q10.5 27 12.5 28.5 L13 52 Q13 55 14 55 Q15 55 15 52 L15.5 28.5 Q17.5 27 17.5 21 Q15.5 25 14 25 Q12.5 25 10.5 21Z"
        fill="url(#cutlery-silver)"
      />
      <ellipse cx="14" cy="53.5" rx="2.8" ry="2.4" fill="url(#cutlery-silver-dark)" opacity="0.6" />
    </svg>
  )
}

/* ── Convenience renderer by type ───────────────────────────── */
export function CutleryIcon({ type, width }: { type: CutleryType; width?: number }) {
  if (type === 'spoon')    return <SpoonIcon    width={width} />
  if (type === 'fork')     return <ForkIcon     width={width} />
  if (type === 'teaspoon') return <TeaspoonIcon width={width} />
  return                          <KnifeIcon    width={width} />
}
